
import React from 'react';
import { Label } from '@/components/ui/label';
import { ClassSchedule, Class } from '@/types/models';
import { TimeField } from '@/components/ui/time-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface ScheduleFormProps {
  dayOfWeek: string;
  setDayOfWeek: (value: string) => void;
  startTime: string;
  setStartTime: (value: string) => void;
  endTime: string;
  setEndTime: (value: string) => void;
  selectedClassId: string;
  setSelectedClassId: (value: string) => void;
  classes?: Class[];
}

const DAYS_OF_WEEK = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

export const ScheduleForm: React.FC<ScheduleFormProps> = ({
  dayOfWeek,
  setDayOfWeek,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  selectedClassId,
  setSelectedClassId,
  classes = [],
}) => {
  const handleTimeChange = (time: Date | undefined, setter: (value: string) => void) => {
    if (time) {
      const timeString = time.toLocaleTimeString('en-US', { 
        hour12: false,
        hour: '2-digit',
        minute: '2-digit'
      });
      setter(timeString);
    }
  };

  const parseTimeString = (timeString: string): Date => {
    const today = new Date();
    const [hours, minutes] = timeString.split(':');
    today.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    return today;
  };

  return (
    <div className="grid gap-4 py-4">
      {classes && classes.length > 0 ? (
        <div className="grid grid-cols-4 items-center gap-4">
          <Label htmlFor="class" className="text-right">
            Class
          </Label>
          <Select 
            value={selectedClassId} 
            onValueChange={setSelectedClassId}
          >
            <SelectTrigger className="col-span-3">
              <SelectValue placeholder="Select a class" />
            </SelectTrigger>
            <SelectContent>
              {classes.map((classItem) => (
                <SelectItem key={classItem.id} value={classItem.id}>
                  {classItem.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <div className="text-center text-amber-500 mb-2">
          No classes available. Please create a class first.
        </div>
      )}

      <div className="grid grid-cols-4 items-center gap-4">
        <Label htmlFor="dayOfWeek" className="text-right">
          Day of Week
        </Label>
        <Select value={dayOfWeek} onValueChange={setDayOfWeek}>
          <SelectTrigger className="col-span-3">
            <SelectValue placeholder="Select a day" />
          </SelectTrigger>
          <SelectContent>
            {DAYS_OF_WEEK.map((day) => (
              <SelectItem key={day.value} value={day.value}>
                {day.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-4 items-center gap-4">
        <Label htmlFor="startTime" className="text-right">
          Start Time
        </Label>
        <div className="col-span-3">
          <TimeField
            value={startTime ? parseTimeString(startTime) : undefined}
            onChange={(date) => handleTimeChange(date, setStartTime)}
            className="w-full"
          />
        </div>
      </div>

      <div className="grid grid-cols-4 items-center gap-4">
        <Label htmlFor="endTime" className="text-right">
          End Time
        </Label>
        <div className="col-span-3">
          <TimeField
            value={endTime ? parseTimeString(endTime) : undefined}
            onChange={(date) => handleTimeChange(date, setEndTime)}
            className="w-full"
          />
        </div>
      </div>
    </div>
  );
};
